import { useState } from "react";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import Icon1 from "@assets/icons/Icon";
import { paymentsApi } from "@services/api/payments";
import { showNotification } from "context/notification.service";
import { PAYMENT_METHOD_LABELS } from "types/payment";
import type { Ordre } from "types/ordre";
import type { Payment, PaymentMethod } from "types/payment";

const TYPE_OPTIONS = [
  { value: "acompte", label: "Acompte" },
  { value: "solde", label: "Solde (paiement final)" },
];

const METHOD_OPTIONS = (Object.keys(PAYMENT_METHOD_LABELS) as PaymentMethod[]).map((k) => ({
  value: k,
  label: PAYMENT_METHOD_LABELS[k],
}));

interface Props {
  ordre: Ordre;
  /** "create" records a brand-new offline payment; "mark" forces an existing request to paid. */
  mode: "create" | "mark";
  payment?: Payment; // required in "mark" mode
  defaultType?: "acompte" | "solde";
  onClose: () => void;
  onSuccess: () => void;
}

export default function ManualPaymentModal({ ordre, mode, payment, defaultType, onClose, onSuccess }: Props) {
  const isMark = mode === "mark";
  const [type, setType] = useState<string>(payment?.type || defaultType || "solde");
  const [amount, setAmount] = useState(
    payment?.amount ? String(payment.amount) : ordre.amount ? String(ordre.amount) : "",
  );
  const [method, setMethod] = useState<PaymentMethod>("especes");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!isMark && (!amount || parseFloat(amount) <= 0)) {
      showNotification({ type: "error", message: "Veuillez saisir un montant valide" });
      return;
    }
    setLoading(true);
    try {
      if (isMark && payment) {
        await paymentsApi.markPaid(payment.id, method, note || undefined);
      } else {
        await paymentsApi.createManual({
          ordre_id: ordre.id,
          type: type as "acompte" | "solde",
          amount: parseFloat(amount),
          method,
          note: note || undefined,
        });
      }
      showNotification({ type: "success", message: "Paiement enregistré" });
      onSuccess();
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || "Erreur lors de l'enregistrement du paiement";
      showNotification({ type: "error", message: detail });
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal
      title={isMark ? "Marquer comme payé" : "Enregistrer un paiement"}
      startIcon={<Icon1 name="payments" size={20} />}
      onClose={onClose}
      width="max-w-md"
    >
      <div className="flex flex-col gap-3 p-4">
        <div
          className="flex flex-row items-center gap-2 p-3 rounded-lg"
          style={{ background: "#EFF6FF", border: "1px solid #BFDBFE" }}
        >
          <span className="text-h4" style={{ color: "#1E40AF" }}>
            {isMark
              ? "Le paiement sera marqué comme réglé sans passer par Bridge (paiement reçu autrement)."
              : "Enregistre un paiement encaissé hors Bridge (espèces, chèque, virement, TPE)."}
          </span>
        </div>

        <DropDown
          label="Type de paiement"
          options={TYPE_OPTIONS}
          value={type}
          onSelect={(v) => setType(String(v))}
          disabled={isMark}
        />

        <TextField
          label="Montant (EUR) *"
          type="number"
          value={amount}
          onChange={setAmount}
          disabled={isMark}
        />

        <DropDown
          label="Moyen de paiement"
          options={METHOD_OPTIONS}
          value={method}
          onSelect={(v) => setMethod(v as PaymentMethod)}
        />

        <TextField
          label="Note (optionnel)"
          value={note}
          onChange={setNote}
        />
      </div>

      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label={loading ? "Enregistrement..." : "Enregistrer le paiement"}
          onClick={handleSubmit}
          disabled={loading}
        />
      </div>
    </BaseModal>
  );
}
